import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  LinearProgress,
  Chip,
  Grid
} from '@mui/material';
import { WbSunny as SunIcon, Bedtime as MoonIcon, Grade as StarIcon, LocalFireDepartment as FireIcon } from '@mui/icons-material';
import { CategoryEnergyStats, EnergyConfigEntry, EnergyResponse } from '../types/energy';
import { energyApi } from '../api/apiService';
import { categories as energyCategories } from '../data/energyCategories';
import {
  getTotalStarsEarned,
  getCurrentEnergyInBar,
  getNextStarThreshold,
  getCelestialCounts,
  getConfigForCategory,
} from '../utils/energyCalculations';

function getCategoryMeta(category: string) {
  const cat = energyCategories.find(c => c.key === category);
  return cat ?? { label: category, icon: '', color: '#999' };
}

const EnergyPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const [stats, setStats] = useState<CategoryEnergyStats[]>([]);
  const [config, setConfig] = useState<EnergyConfigEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (userId) {
      fetchEnergy();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchEnergy = async () => {
    try {
      setLoading(true);
      const res = await energyApi.getUserEnergy(userId!);
      const data: EnergyResponse = res.data;
      setStats(data.stats || []);
      setConfig(data.config || []);
    } catch (err) {
      console.error('Failed to load energy:', err);
      setError('Failed to load energy');
    } finally {
      setLoading(false);
    }
  };

  // Sum total stars across all categories, then apply global conversion
  const getTotals = (): { stars: number; moons: number; suns: number } => {
    let totalStars = 0;
    for (const s of stats) {
      const cfg = getConfigForCategory(config, s.category);
      if (!cfg) continue;
      totalStars += getTotalStarsEarned(s.totalEnergy, cfg.initialThreshold, cfg.growthRate);
    }
    return getCelestialCounts(totalStars);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!userId) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Typography color="text.secondary">No user selected</Typography>
      </Box>
    );
  }

  const hasAnyEnergy = stats.length > 0 && stats.some(s => s.totalEnergy > 0);

  return (
    <Box>
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>{error}</Alert>}

      {/* Header */}
      <Card sx={{ mb: 3, borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{
          background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
          px: 3,
          py: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Box sx={{
            bgcolor: 'rgba(255,255,255,0.2)',
            borderRadius: 2,
            p: 1.5,
            display: 'flex'
          }}>
            <SunIcon sx={{ color: 'white', fontSize: 32 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
              My Energy
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {(() => {
                const totals = getTotals();
                return (
                  <>
                    {/* Suns */}
                    {totals.suns > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <SunIcon sx={{ fontSize: 20, color: '#ffd700' }} />
                        {totals.suns > 1 && (
                          <Typography variant="caption" sx={{ color: 'white', fontWeight: 700 }}>
                            ×{totals.suns}
                          </Typography>
                        )}
                      </Box>
                    )}
                    {/* Moons */}
                    {Array.from({ length: totals.moons }).map((_, i) => (
                      <MoonIcon key={`moon-${i}`} sx={{ fontSize: 20, color: '#ffd700' }} />
                    ))}
                    {/* Stars */}
                    {Array.from({ length: totals.stars }).map((_, i) => (
                      <StarIcon key={`star-${i}`} sx={{ fontSize: 20, color: '#ffd700' }} />
                    ))}
                    {totals.suns === 0 && totals.moons === 0 && totals.stars === 0 && (
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        No stars yet
                      </Typography>
                    )}
                  </>
                );
              })()}
            </Box>
          </Box>
        </Box>
      </Card>

      {/* Category Grid - sorted by energy descending */}
      <Grid container spacing={2}>
        {hasAnyEnergy ? (
          stats.filter(s => s.totalEnergy > 0).map((stat) => {
            const cfg = getConfigForCategory(config, stat.category);
            if (!cfg) return null;
            const meta = getCategoryMeta(stat.category);

            const totalStars = getTotalStarsEarned(stat.totalEnergy, cfg.initialThreshold, cfg.growthRate);
            const currentEnergy = getCurrentEnergyInBar(stat.totalEnergy, cfg.initialThreshold, cfg.growthRate);
            const nextThreshold = getNextStarThreshold(stat.totalEnergy, cfg.initialThreshold, cfg.growthRate);
            const counts = getCelestialCounts(totalStars);
            const progressPercent = nextThreshold > 0 ? (currentEnergy / nextThreshold) * 100 : 0;

            return (
              <Grid item xs={12} sm={6} md={4} key={stat.category}>
                <Card sx={{
                  borderRadius: 3,
                  border: `1px solid ${meta.color}40`,
                  bgcolor: meta.color + '08'
                }}>
                  <CardContent sx={{ p: 3 }}>
                    {/* Category Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <Typography variant="h4">{meta.icon}</Typography>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {meta.label}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Energy Bar */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {Math.floor(currentEnergy)} / {Math.floor(nextThreshold)} to next star
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={progressPercent}
                        sx={{
                          height: 10,
                          borderRadius: 5,
                          bgcolor: 'grey.100',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: meta.color,
                            borderRadius: 5,
                          }
                        }}
                      />
                    </Box>

                    {/* Sun / Moon / Star counters (all yellow) */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                      {/* Suns (with ×count if >1) */}
                      {counts.suns > 0 && (
                        <>
                          <SunIcon sx={{ fontSize: 24, color: '#ffd700' }} />
                          {counts.suns > 1 && (
                            <Typography variant="body2" fontWeight={700} color="#ffd700">
                              ×{counts.suns}
                            </Typography>
                          )}
                        </>
                      )}
                      {/* Moons */}
                      {Array.from({ length: counts.moons }).map((_, i) => (
                        <MoonIcon key={`moon-${i}`} sx={{ fontSize: 24, color: '#ffd700' }} />
                      ))}
                      {/* Stars - show actual stars (yellow), or one grey star if total is 0 */}
                      {totalStars === 0 ? (
                        <StarIcon sx={{ fontSize: 24, color: 'grey.300' }} />
                      ) : (
                        Array.from({ length: counts.stars }).map((_, i) => (
                          <StarIcon key={`star-${i}`} sx={{ fontSize: 24, color: '#ffd700' }} />
                        ))
                      )}
                    </Box>

                    {/* Combo streak */}
                    {stat.comboDays > 0 && (
                      <Chip
                        icon={<FireIcon sx={{ fontSize: 16 }} />}
                        label={`${stat.comboDays} day streak ×${stat.comboMultiplier.toFixed(1)}`}
                        size="small"
                        sx={{
                          bgcolor: '#ff6b6b15',
                          color: '#f97316',
                          fontWeight: 600,
                          border: '1px solid #f9731630',
                          fontSize: '0.7rem'
                        }}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })
        ) : null}
      </Grid>

      {/* Empty State */}
      {!hasAnyEnergy && (
        <Card sx={{ borderRadius: 3, p: 6, textAlign: 'center' }}>
          <SunIcon sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No energy yet
          </Typography>
          <Typography variant="body2" color="text.disabled">
            Complete targets with categories to start earning energy!
          </Typography>
        </Card>
      )}
    </Box>
  );
};

export default EnergyPage;
